// ═══════════════════════════════════════════════════════════════════════
// connector-setup-draft.ts — localStorage draft for the Integration Hub's
// in-progress connector-setup builder (the acceptedApps list a user assembles
// before handing off to the Architect via explainAcceptedApps).
// ═══════════════════════════════════════════════════════════════════════
// Fixes connector amnesia (Jacob #7): the builder was React-state-only, so
// leaving or reloading the Integration Hub wiped what the user was setting up —
// "went back, no context on connecting Gmail". This persists the draft per-OS
// so a return restores the list and the agent still has that context.
//
// Mirrors build-storage.ts (the intake-draft pattern): one key per OS, a size
// cap via a 14-day expiry, best-effort (never throws — telemetry/drafts must
// never break the app). Keyed per-OS; the hub's builder is a single list for
// the OS (not per-connector), so per-OS keying is correct.
// ═══════════════════════════════════════════════════════════════════════

const KEY_PREFIX = "sloe.connector_setup.";
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // expire abandoned drafts after 14 days

export interface ConnectorSetupDraft {
  acceptedApps: string[];
  appDraft: string;
  updated_at: number; // epoch ms
}

// Load the draft for an OS. Null if missing, corrupt, or expired (expired rows
// are pruned on read).
export function loadConnectorDraft(osId: string): ConnectorSetupDraft | null {
  if (typeof window === "undefined" || !osId) return null;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + osId);
    if (!raw) return null;
    const d = JSON.parse(raw) as ConnectorSetupDraft;
    if (!d || !Array.isArray(d.acceptedApps)) return null;
    if (typeof d.updated_at === "number" && Date.now() - d.updated_at > MAX_AGE_MS) {
      localStorage.removeItem(KEY_PREFIX + osId);
      return null;
    }
    return { acceptedApps: d.acceptedApps.map(String), appDraft: String(d.appDraft || ""), updated_at: d.updated_at || 0 };
  } catch {
    return null;
  }
}

// Save (debounced by the caller). An empty draft removes the key — nothing to
// resume, so we don't leave a stale row behind.
export function saveConnectorDraft(osId: string, draft: { acceptedApps: string[]; appDraft: string }): void {
  if (typeof window === "undefined" || !osId) return;
  try {
    if (!draft.acceptedApps.length && !draft.appDraft.trim()) {
      localStorage.removeItem(KEY_PREFIX + osId);
      return;
    }
    localStorage.setItem(KEY_PREFIX + osId, JSON.stringify({ ...draft, updated_at: Date.now() }));
  } catch {
    // localStorage full or disabled — fail silently, drafts must never crash.
  }
}

// Clear the draft (called once a connection completes — the setup is done).
export function clearConnectorDraft(osId: string): void {
  if (typeof window === "undefined" || !osId) return;
  try {
    localStorage.removeItem(KEY_PREFIX + osId);
  } catch {
    // ignore
  }
}
